import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import {
  buildGoogleEarthUrl,
  buildTimOnlineUrl,
  formatFullAddress,
  timOnlineMoeglich,
  buildHochwasserCheckUrl,
  hochwasserCheckMoeglich,
  HOCHWASSER_HINWEIS,
} from "../../utils/geoLinks";
import { useGeocodedAddress } from "../forms/google/useGeocodedAddress";
import AdressFelder from "./AdressFelder";

const useStyles = makeStyles({
  card: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  linkRow: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "10px",
    rowGap: "8px",
    paddingTop: "4px",
  },
  linkBtn: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.bg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "14px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("999px"),
    textDecorationLine: "none",
    transitionProperty: "background-color, border-color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: "#f5eef6",
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  linkBtnDisabled: {
    cursor: "not-allowed",
    opacity: 0.4,
  },
});

const EarthIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" stroke={BRAND.purple} strokeWidth="1.4" />
    <path d="M1.4 8h13.2M8 1.4c2 2.4 3 5 3 6.6s-1 4.2-3 6.6c-2-2.4-3-5-3-6.6s1-4.2 3-6.6z" stroke={BRAND.purple} strokeWidth="1.2" fill="none" />
  </svg>
);

const NrwIcon = () => (
  <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
    <path d="M2 2h12v8c0 4-3 6-6 7-3-1-6-3-6-7V2z" fill={BRAND.gold} stroke={BRAND.ink} strokeWidth="1" />
    <path d="M5 6c1 0 2 1 2 2v2c0 1-1 2-2 2s-2-1-2-2V8c0-1 1-2 2-2zM11 6c1 0 2 1 2 2v2c0 1-1 2-2 2" stroke={BRAND.ink} strokeWidth="0.8" fill="none" />
  </svg>
);

/** Tropfen über einer Welle — Hochwasser-Check (T-339). */
const FlutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 1.6C6 4.4 4.6 6.3 4.6 8.1a3.4 3.4 0 0 0 6.8 0C11.4 6.3 10 4.4 8 1.6z" stroke={BRAND.purple} strokeWidth="1.4" />
    <path d="M1.4 14c1.1 0 1.6-.8 2.2-.8s1.1.8 2.2.8 1.6-.8 2.2-.8 1.1.8 2.2.8 1.6-.8 2.2-.8 1.1.8 2.2.8" stroke={BRAND.purple} strokeWidth="1.2" />
  </svg>
);

type AnschriftFeld = "street" | "postalCode" | "city" | "country";

type Props = {
  street: string;
  postalCode: string;
  city: string;
  country: string;
  onChange: (key: AnschriftFeld, value: string) => void;
  /**
   * Adresse während der Eingabe (debounced) an Google geokodieren, damit
   * Google Earth in geneigter 3D-Ansicht öffnet. Ohne Koordinaten bleibt es
   * beim bisherigen Such-Link. Der TIM-online-Link nutzt weiterhin den
   * Adressparameter (siehe `buildTimOnlineUrl`).
   */
  geocode?: boolean;
};

const AnschriftBlock = ({ street, postalCode, city, country, onChange, geocode = false }: Props) => {
  const styles = useStyles();
  const adresse = { street, postalCode, city, country };
  const fullAddress = formatFullAddress(adresse);
  const hasAddr = fullAddress.trim().length > 0;
  const coords = useGeocodedAddress(adresse, geocode);
  // Der "#"-Zweig bleibt: sonst öffneten Mittelklick / "In neuem Tab" eine
  // leere Such-URL bzw. ein "?adresse=,".
  const earthHref = hasAddr ? buildGoogleEarthUrl(fullAddress, coords) : "#";
  const timHref = hasAddr ? buildTimOnlineUrl(adresse) : "#";
  const hochwasserHref = hasAddr ? buildHochwasserCheckUrl(adresse) : "#";

  return (
    <div className={styles.card}>
      <AdressFelder
        idPrefix="risikoanschrift"
        keys={{
          street: "street",
          postalCode: "postalCode",
          city: "city",
          country: "country",
        }}
        values={{ street, postalCode, city, country }}
        onChange={(k, v) => onChange(k as AnschriftFeld, v)}
      />
      <div className={styles.linkRow}>
        {timOnlineMoeglich(adresse) && (
          <a
            className={mergeClasses(styles.linkBtn, !hasAddr && styles.linkBtnDisabled)}
            href={timHref}
            target={hasAddr ? "_blank" : undefined}
            rel="noopener noreferrer"
            onClick={(e) => {
              if (!hasAddr) e.preventDefault();
            }}
          >
            <NrwIcon />
            TIM-online ansehen
          </a>
        )}
        {/* T-339: GDV-Hochwasser-Check, nur verlinkt; ZÜRS-Hinweis im Tooltip. */}
        {hochwasserCheckMoeglich(adresse) && (
          <a
            className={mergeClasses(styles.linkBtn, !hasAddr && styles.linkBtnDisabled)}
            href={hochwasserHref}
            target={hasAddr ? "_blank" : undefined}
            rel="noopener noreferrer"
            title={HOCHWASSER_HINWEIS}
            onClick={(e) => {
              if (!hasAddr) e.preventDefault();
            }}
          >
            <FlutIcon />
            Hochwasser-Check
          </a>
        )}
        <a
          className={mergeClasses(styles.linkBtn, !hasAddr && styles.linkBtnDisabled)}
          href={earthHref}
          target={hasAddr ? "_blank" : undefined}
          rel="noopener noreferrer"
          onClick={(e) => {
            if (!hasAddr) e.preventDefault();
          }}
        >
          <EarthIcon />
          In Google Earth ansehen
        </a>
      </div>
    </div>
  );
};

export default AnschriftBlock;
